import { PickSampleExt } from "@novorender/api";
import { ReadonlyVec3 } from "gl-matrix";
import { useCallback, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { coordinatesActions, selectCoordinates } from "features/coordinates/coordinatesSlice";
import { useAddCoordinate } from "features/coordinates/hooks/useAddCoordinate";
import { measureActions, selectMeasurePickSettings } from "features/measure";
import { selectCurrentPickerGeneratedParametricData } from "features/render";
import { Picker } from "features/render/types";
import { applyCameraDistanceToMeasureTolerance } from "features/render/utils";
import { undoRedoActions } from "features/undoRedo";
import { hasMouseSupport } from "utils/misc";

import {
    mobileMeasureActions,
    mobileMeasureCursorRef,
    PICK_TARGET_OFFSET,
    selectCursorPosition,
} from "./mobileMeasureSlice";
import { useMobileMeasureCursor } from "./useMobileMeasureCursor";

const TAP_PROXIMITY_SQ = 30 * 30;

export function useMobileCoordinate() {
    const dispatch = useAppDispatch();
    const {
        state: { view },
    } = useExplorerGlobals();
    const addCoordinate = useAddCoordinate();
    const coordinates = useAppSelector(selectCoordinates);
    const coordinatesRef = useRef(coordinates);
    coordinatesRef.current = coordinates;
    const cursorPos = useAppSelector(selectCursorPosition);
    const cursorPosRef = useRef(cursorPos);
    cursorPosRef.current = cursorPos;
    const movingCoordIdRef = useRef<string | null>(null);
    const measurePickSettings = useAppSelector(selectMeasurePickSettings);
    const allowGeneratedParametric = useAppSelector(selectCurrentPickerGeneratedParametricData);

    const onDragUpdate = useCallback(
        async (result: PickSampleExt | null) => {
            if (!view || !result || result.objectId === -1) {
                dispatch(measureActions.selectHoverObj(undefined));
                dispatch(mobileMeasureActions.setCursorVisual("noHit"));
                return;
            }

            dispatch(
                mobileMeasureActions.setCursorVisual(result.sampleType === "surface" ? "surfaceSnap" : "pointSnap"),
            );

            const tolerance = applyCameraDistanceToMeasureTolerance(
                result.position,
                view.renderState.camera,
                measurePickSettings,
            );
            const entity = await view.measure?.core.pickMeasureEntity(
                result.objectId,
                result.position,
                tolerance,
                allowGeneratedParametric.enabled,
            );

            if (entity?.entity && entity.connectionPoint) {
                dispatch(
                    measureActions.selectHoverObj({
                        ...entity.entity,
                        connectionPoint: entity.connectionPoint,
                    }),
                );
            } else {
                dispatch(measureActions.selectHoverObj(undefined));
            }
        },
        [view, dispatch, measurePickSettings, allowGeneratedParametric],
    );

    const onPlace = useCallback(
        async (result: PickSampleExt) => {
            if (!view) {
                return;
            }

            if (result.objectId === -1) {
                return;
            }

            // If we're moving a coordinate, this placement finishes the move
            const movingId = movingCoordIdRef.current ?? mobileMeasureCursorRef.movingCoordinateId;
            if (movingId) {
                mobileMeasureCursorRef.movingCoordinateId = null;
                const oldCoord = coordinatesRef.current.find((c) => c.id === movingId);
                movingCoordIdRef.current = null;

                const coordinateId = crypto.randomUUID();
                const added = await addCoordinate(result.position, {
                    screenOffset: { x: result.x, y: result.y },
                    normal: result.normal,
                    coordinateId,
                    objectId: result.objectId,
                });

                if (added !== false && oldCoord) {
                    dispatch(coordinatesActions.removeCoordinate(movingId));
                    dispatch(
                        undoRedoActions.push({
                            description: "Move coordinate",
                            undoCallback: () => {
                                dispatch(coordinatesActions.removeCoordinate(coordinateId));
                                dispatch(coordinatesActions.addCoordinate(oldCoord));
                            },
                            redoCallback: () => {
                                dispatch(coordinatesActions.removeCoordinate(oldCoord.id));
                                if (added) {
                                    dispatch(coordinatesActions.addCoordinate(added));
                                }
                            },
                        }),
                    );
                }
                dispatch(mobileMeasureActions.setSelectedCoordinateId(null));
                dispatch(mobileMeasureActions.setDpadStyle("normal"));
                dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
                return;
            }

            const coordinateId = crypto.randomUUID();
            const added = await addCoordinate(result.position, {
                screenOffset: { x: result.x, y: result.y },
                normal: result.normal,
                coordinateId,
                objectId: result.objectId,
            });

            if (added !== false) {
                dispatch(mobileMeasureActions.setSelectedCoordinateId(null));
                dispatch(mobileMeasureActions.setDpadStyle("normal"));
                dispatch(mobileMeasureActions.setCursorVisual("crosshair"));

                dispatch(
                    undoRedoActions.push({
                        description: "Add coordinate",
                        undoCallback: () => {
                            dispatch(coordinatesActions.removeCoordinate(coordinateId));
                        },
                        redoCallback: () => {
                            if (added) {
                                dispatch(coordinatesActions.addCoordinate(added));
                            }
                        },
                    }),
                );
            }
        },
        [view, addCoordinate, dispatch],
    );

    const onTapElsewhere = useCallback(() => {
        movingCoordIdRef.current = null;
        mobileMeasureCursorRef.movingCoordinateId = null;
        dispatch(mobileMeasureActions.setSelectedCoordinateId(null));
        dispatch(mobileMeasureActions.setDpadStyle("normal"));
        dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
        dispatch(mobileMeasureActions.setIndicatorTransform(null));
    }, [dispatch]);

    const onDpadLongPress = useCallback(() => {
        if (!view) {
            return;
        }
        const pos = cursorPosRef.current;
        if (!pos) {
            return;
        }
        const pickPos = {
            x: pos.x + PICK_TARGET_OFFSET.x,
            y: pos.y + PICK_TARGET_OFFSET.y,
        };
        const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false });
        const coords = coordinatesRef.current;
        for (const coord of coords) {
            const sp = toScreenSpace(coord.point.position as ReadonlyVec3);
            if (!sp) {
                continue;
            }
            const dx1 = pos.x - sp[0];
            const dy1 = pos.y - sp[1];
            const dx2 = pickPos.x - sp[0];
            const dy2 = pickPos.y - sp[1];
            if (dx1 * dx1 + dy1 * dy1 < TAP_PROXIMITY_SQ || dx2 * dx2 + dy2 * dy2 < TAP_PROXIMITY_SQ) {
                movingCoordIdRef.current = coord.id;
                dispatch(mobileMeasureActions.setSelectedCoordinateId(coord.id));
                const dpadX = sp[0] - PICK_TARGET_OFFSET.x;
                const dpadY = sp[1] - PICK_TARGET_OFFSET.y;
                dispatch(mobileMeasureActions.setCursorPosition({ x: dpadX, y: dpadY }));
                dispatch(mobileMeasureActions.setIndicatorPosition({ x: sp[0], y: sp[1] }));
                dispatch(mobileMeasureActions.setDpadStyle("normal"));
                dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
                return;
            }
        }
    }, [view, dispatch]);

    useMobileMeasureCursor({
        picker: Picker.Coordinates,
        onPlace,
        onDragUpdate,
        onTapElsewhere,
        onDpadLongPress,
        enabled: !hasMouseSupport(),
        sampleDiscRadius: 4,
        persistent: true,
    });
}
